import { OffersResponse } from "./OfferList";
import { Resource } from "../api/useFetch";
import { Offer, Location } from "./offer";
import { ShiftOffer } from "./shiftOffer";
import { OfferItem } from "./offerItem";
import { BookingType } from "BookingType";
import {
  MarketplaceOffer,
  MarketplaceOffersResponse,
  MarketplaceLocation,
} from "./marketplaceTypes";

/**
 * Transform Marketplace API response to the OfferItem[] discriminated union
 * consumed by the offers page. Ride items resolve to Resource<Offer>, shift
 * items resolve to Resource<ShiftOffer>.
 */
export function transformMarketplaceToJsonApi(
  marketplaceResponse: MarketplaceOffersResponse,
): OffersResponse {
  const locationMap = new Map<string, Resource<Location>>();
  const items: OfferItem[] = [];

  if (!marketplaceResponse?.items) {
    console.warn(
      "[Marketplace Adapter] No items found in response:",
      marketplaceResponse,
    );
    return {
      data: [],
      included: [],
      meta: {
        record_count: 0,
        page_count: 0,
      },
    };
  }

  marketplaceResponse.items.forEach((marketplaceOffer) => {
    if (!isOfferShapeValid(marketplaceOffer)) {
      return;
    }

    if (marketplaceOffer.type === "shift") {
      const shiftItem = buildShiftItem(marketplaceOffer);
      if (shiftItem) {
        items.push(shiftItem);
      }
      return;
    }

    const rideItem = buildRideItem(marketplaceOffer, locationMap);
    if (rideItem) {
      items.push(rideItem);
    }
  });

  return {
    data: items,
    included: Array.from(locationMap.values()),
    meta: {
      record_count: marketplaceResponse.total_items,
      page_count: marketplaceResponse.total_pages,
    },
  };
}

function isOfferShapeValid(marketplaceOffer: MarketplaceOffer): boolean {
  if (!marketplaceOffer.id) {
    console.warn("[Marketplace Adapter] Skipping offer without ID");
    return false;
  }

  if (
    typeof marketplaceOffer.price !== "number" ||
    isNaN(marketplaceOffer.price)
  ) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: invalid or missing price`,
    );
    return false;
  }

  if (!marketplaceOffer.currency) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing currency`,
    );
    return false;
  }

  if (!marketplaceOffer.vehicleClass) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing vehicleClass`,
    );
    return false;
  }

  return true;
}

function buildRideItem(
  marketplaceOffer: MarketplaceOffer,
  locationMap: Map<string, Resource<Location>>,
): OfferItem | null {
  if (!marketplaceOffer.rides || marketplaceOffer.rides.length === 0) {
    console.warn(
      `[Marketplace Adapter] Skipping ride offer ${marketplaceOffer.id}: no rides`,
    );
    return null;
  }

  const primaryRide = marketplaceOffer.rides[0];

  if (!primaryRide.pickupTime) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing pickupTime`,
    );
    return null;
  }

  if (!primaryRide.pickUpLocation) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: missing pickUpLocation`,
    );
    return null;
  }

  if (
    typeof primaryRide.pickUpLocation.longitude !== "number" ||
    typeof primaryRide.pickUpLocation.latitude !== "number"
  ) {
    console.warn(
      `[Marketplace Adapter] Skipping offer ${marketplaceOffer.id}: invalid pickUpLocation coordinates`,
    );
    return null;
  }

  const pickupLocationId = buildLocationId(primaryRide.pickUpLocation);
  const dropoffLocationId = primaryRide.dropOffLocation
    ? buildLocationId(primaryRide.dropOffLocation)
    : undefined;

  addLocationToMap(locationMap, pickupLocationId, primaryRide.pickUpLocation);
  if (primaryRide.dropOffLocation && dropoffLocationId) {
    addLocationToMap(
      locationMap,
      dropoffLocationId,
      primaryRide.dropOffLocation,
    );
  }

  const offer: Resource<Offer> = {
    id: marketplaceOffer.id,
    attributes: {
      starts_at: primaryRide.pickupTime,
      ends_at: "",
      service_class: mapVehicleClass(marketplaceOffer.vehicleClass),
      price: marketplaceOffer.price.toFixed(2),
      currency: marketplaceOffer.currency,
      distance: primaryRide.estimatedDistanceMeters || 0,
      duration:
        primaryRide.type === "hourly"
          ? primaryRide.durationMinutes
            ? primaryRide.durationMinutes * 60
            : undefined
          : primaryRide.estimatedDurationMinutes
            ? primaryRide.estimatedDurationMinutes * 60
            : undefined,
      booking_type: mapBookingType(primaryRide.type),
      is_final_price: true,
      next_price_change_in: 0,
      special_requests: primaryRide.guestRequests || undefined,
      flight_number: primaryRide.flightNumber || undefined,
    },
    relationships: {
      pickup_location: { data: { id: pickupLocationId } },
      ...(dropoffLocationId && {
        dropoff_location: { data: { id: dropoffLocationId } },
      }),
    },
  };

  return { kind: "ride", resource: offer };
}

function buildShiftItem(marketplaceOffer: MarketplaceOffer): OfferItem | null {
  const shift = marketplaceOffer.shifts?.[0];

  if (!shift) {
    console.warn(
      `[Marketplace Adapter] Skipping shift offer ${marketplaceOffer.id}: no shifts`,
    );
    return null;
  }

  if (!shift.start_time || !shift.end_time) {
    console.warn(
      `[Marketplace Adapter] Skipping shift offer ${marketplaceOffer.id}: missing start_time/end_time`,
    );
    return null;
  }

  const confirmation = marketplaceOffer.confirmations?.[0];

  const resource: Resource<ShiftOffer> = {
    id: marketplaceOffer.id,
    attributes: {
      starts_at: shift.start_time,
      ends_at: shift.end_time,
      operating_area: shift.operating_area,
      max_shift_duration_hours: shift.max_shift_duration_hours,
      is_flexible_shift: shift.is_flexible_shift,
      description: shift.description,
      service_class: mapVehicleClass(marketplaceOffer.vehicleClass),
      price: marketplaceOffer.price.toFixed(2),
      currency: marketplaceOffer.currency,
      subtitle: marketplaceOffer.subtitle,
      banner: marketplaceOffer.banner,
      confirmation: confirmation
        ? {
            title: confirmation.title,
            description: confirmation.description,
            button_label: confirmation.button_label,
          }
        : undefined,
    },
  };

  return { kind: "shift", resource };
}

function buildLocationId(location: MarketplaceLocation): string {
  const key = location.address ?? `${location.longitude},${location.latitude}`;
  return `mp-${key}`;
}

function addLocationToMap(
  map: Map<string, Resource<Location>>,
  id: string,
  location: MarketplaceLocation,
) {
  if (!map.has(id)) {
    map.set(id, {
      id,
      attributes: {
        formatted_address_en: location.address,
      },
    });
  }
}

function mapVehicleClass(vehicleClass: string): string {
  const mapping: Record<string, string> = {
    business: "business",
    electric_class: "electric_class",
    first: "first",
    business_van: "business_van",
  };

  return mapping[vehicleClass] || vehicleClass.toLowerCase();
}

function mapBookingType(type: string): BookingType {
  const typeMap: Record<string, BookingType> = {
    transfer: "transfer",
    hourly: "hourly",
  };

  return typeMap[type.toLowerCase()] || "transfer";
}
